import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useProjects } from '../hooks/useData';
import { B, FONT } from '../lib/constants';
import Dashboard from './Dashboard';
import ProjectDetail from './ProjectDetail';
import DesktopLayout from './DesktopLayout';

export default function BuilderApp({ isAssistant = false }) {
  const { user } = useAuth();
  const { permission, requestPermission } = usePushNotifications();
  const { projects, loading, updateProject, refetch } = useProjects();
  const [view, setView] = useState('dashboard');
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [tab, setTabState] = useState('overview');
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const handle = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  // Restore state from history on load (e.g. after refresh)
  useEffect(() => {
    const state = window.history.state;
    if (state?.view === 'project' && state?.projectId) {
      setView('project');
      setActiveProjectId(state.projectId);
      setTabState(state.tab || 'overview');
    }
  }, []);

  // Listen for back/forward button (hardware back gesture, browser back)
  useEffect(() => {
    const onPopState = (e) => {
      const state = e.state;
      if (!state || state.view === 'dashboard') {
        setView('dashboard');
        setActiveProjectId(null);
        setTabState('overview');
      } else if (state.view === 'project') {
        setView('project');
        setActiveProjectId(state.projectId);
        setTabState(state.tab || 'overview');
      }
    };
    window.addEventListener('popstate', onPopState);
    // Set initial history state so back button works from dashboard too
    if (!window.history.state) {
      window.history.replaceState({ view: 'dashboard' }, '');
    }
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Deep link: notifications carry ?project=X&tab=Y — open that project/tab once projects load
  useEffect(() => {
    if (!projects.length) return;
    const params = new URLSearchParams(window.location.search);
    const pid = params.get('project');
    const deepTab = params.get('tab');
    if (pid && projects.find(p => String(p.id) === String(pid))) {
      setActiveProjectId(parseInt(pid));
      setView('project');
      setTabState(deepTab || 'overview');
      window.history.replaceState({ view: 'project', projectId: parseInt(pid), tab: deepTab || 'overview' }, '', window.location.pathname);
    } else if (pid || deepTab) {
      window.history.replaceState({ view: 'dashboard' }, '', window.location.pathname);
    }
  }, [projects.length]);

  const activeProject = projects.find(p => p.id === activeProjectId);

  const openProject = useCallback((id, openTab = 'overview') => {
    setActiveProjectId(id);
    setTabState(openTab);
    setView('project');
    window.history.pushState({ view: 'project', projectId: id, tab: openTab }, '');
  }, []);

  // Tab change inside a project — push a new history entry so back returns to previous tab
  const setTab = useCallback((newTab) => {
    setTabState(newTab);
    const current = window.history.state;
    if (current?.view === 'project' && current.tab !== newTab) {
      window.history.pushState({ ...current, tab: newTab }, '');
    }
  }, []);

  // Swipe uses replaceState so back button always goes home, not to previous build
  const swipeProject = useCallback((id, openTab = 'overview') => {
    setActiveProjectId(id);
    setTabState(openTab);
    setView('project');
    window.history.replaceState({ view: 'project', projectId: id, tab: openTab }, '');
  }, []);

  // Home button — always jumps to dashboard
  const goHome = useCallback(() => {
    setView('dashboard');
    setActiveProjectId(null);
    setTabState('overview');
    window.history.pushState({ view: 'dashboard' }, '');
  }, []);

  // Back button — pops one entry off the history stack
  // (popstate listener above will restore whatever view/tab/project came before)
  const goBack = useCallback(() => {
    window.history.back();
  }, []);

  if (loading && projects.length === 0) return (
    <div style={{ minHeight: '100vh', background: B.black, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT }}>
      <div style={{ color: '#6F6F6F', fontSize: 13 }}>Loading builds…</div>
    </div>
  );

  const notifBanner = permission !== 'granted' ? (
    <div onClick={requestPermission}
      style={{ background: 'rgba(248,218,107,0.1)', border: '1px solid #9A732F', borderRadius: 10, padding: '10px 16px', margin: '10px 12px 0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontFamily: FONT }}>
      <span style={{ fontSize: 16 }}>🔔</span>
      <span style={{ color: '#F8DA6B', fontSize: 13, fontWeight: 700 }}>Tap to enable notifications</span>
      <span style={{ color: '#6F6F6F', fontSize: 11, marginLeft: 'auto' }}>decisions · notes · purchases</span>
    </div>
  ) : null;

  // Desktop gets its own layout
  if (isDesktop) {
    return (
      <div>
        {notifBanner}
        <DesktopLayout
        projects={projects}
        onOpenProject={openProject}
        onGoHome={goHome}
        onGoBack={goBack}
        onRefresh={refetch}
        isAssistant={isAssistant}
      />
      </div>
    );
  }

  // Mobile layout
  return (
    <div style={{ minHeight: '100vh', background: B.black, fontFamily: FONT }}>
      {notifBanner}

      {/* Floating home button — visible on every screen except the dashboard */}
      {view !== 'dashboard' && (
        <button onClick={goHome}
          style={{ position: 'fixed', bottom: 20, right: 16, zIndex: 900, background: '#F8DA6B', color: '#000', border: 'none', borderRadius: '50%', width: 48, height: 48, cursor: 'pointer', fontSize: 20, boxShadow: '0 4px 16px rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          🏠
        </button>
      )}

      {view === 'dashboard' && (
        <Dashboard
          projects={projects}
          onOpenProject={openProject}
          onRefresh={refetch}
          isAssistant={isAssistant}
        />
      )}
      {view === 'project' && activeProject && (
        <ProjectDetail
          project={activeProject}
          allProjects={projects}
          tab={tab}
          setTab={setTab}
          onGoHome={goHome}
          onGoBack={goBack}
          onSwipeProject={swipeProject}
          onUpdateProject={(updates) => updateProject(activeProject.id, updates)}
          onRefresh={refetch}
        />
      )}
    </div>
  );
}
